// Светлый текст интерфейса — тот же цвет, что --text-primary-color в App.css
export const LIGHT_TEXT_COLOR = '#e0e0e0';

// Наименьший контраст крупного текста с фоном, при котором он читается (WCAG AA).
// Порог, а не «что контрастнее»: на цветах средней яркости — темно-зеленом
// (3.05), рубине (3.15), синем (3.22) — формально контрастнее темный текст, но
// светлый на них привычнее и читается, поэтому он остается
export const MIN_TEXT_CONTRAST_RATIO = 3;

const HEX_COLOR_REGEXP = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const CSS_VARIABLE_REGEXP = /^var\(\s*(--[\w-]+)\s*(?:,\s*(.+?)\s*)?\)$/;

// «var(--имя)» -> значение переменной у корня документа; без документа или
// без переменной — запасное значение из самой записи var(--имя, запас)
const resolveCssVariable = (color) => {
  const match = CSS_VARIABLE_REGEXP.exec(color);
  if (!match) {
    return color;
  }
  const [, variableName, fallbackValue] = match;
  const rootValue = typeof document !== 'undefined'
    ? window.getComputedStyle(document.documentElement).getPropertyValue(variableName).trim()
    : '';
  return rootValue || fallbackValue || null;
};

// '#rgb' или '#rrggbb' -> каналы 0..1; другое — null
const parseHexColor = (color) => {
  const resolvedColor = typeof color === 'string' ? resolveCssVariable(color.trim()) : null;
  if (!resolvedColor || !HEX_COLOR_REGEXP.test(resolvedColor)) {
    return null;
  }
  const digits = resolvedColor.slice(1);
  const fullDigits = digits.length === 3 ? digits.split('').map((digit) => digit + digit).join('') : digits;
  return [0, 2, 4].map((offset) => parseInt(fullDigits.slice(offset, offset + 2), 16) / 255);
};

/**
 * Относительная яркость цвета по WCAG: 0 — черный, 1 — белый
 * @param {string} color - '#rgb', '#rrggbb' или 'var(--css-переменная)' с таким значением
 * @returns {number|null} null — цвет не разобран
 */
export const getColorLuminance = (color) => {
  const channels = parseHexColor(color);
  if (!channels) {
    return null;
  }
  const [red, green, blue] = channels.map(
    (channel) => (channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4)
  );
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
};

/**
 * Контраст двух цветов по WCAG: от 1 (одинаковые) до 21 (черный с белым)
 * @param {string} colorA
 * @param {string} colorB
 * @returns {number|null} null — какой-то из цветов не разобран
 */
export const getColorContrastRatio = (colorA, colorB) => {
  const luminanceA = getColorLuminance(colorA);
  const luminanceB = getColorLuminance(colorB);
  if (luminanceA === null || luminanceB === null) {
    return null;
  }
  return (Math.max(luminanceA, luminanceB) + 0.05) / (Math.min(luminanceA, luminanceB) + 0.05);
};

/**
 * Нужен ли темный текст на фоне такого цвета. Интерфейс темный, и текст в нем
 * по умолчанию светлый (LIGHT_TEXT_COLOR), но на ярком фоне — желтом,
 * оранжевом, голубом, салатовом — он сливается: там контраст ниже
 * MIN_TEXT_CONTRAST_RATIO. На красном, фиолетовом, темно-зеленом светлый
 * текст читается и остается.
 *
 * Неразобранный цвет (нет значения, не hex) темного текста не требует:
 * оформление остается таким, каким было без подсказки.
 *
 * @param {string} backgroundColor
 * @returns {boolean}
 */
export const checkIfColorNeedsDarkText = (backgroundColor) => {
  const lightTextContrastRatio = getColorContrastRatio(LIGHT_TEXT_COLOR, backgroundColor);
  return lightTextContrastRatio !== null && lightTextContrastRatio < MIN_TEXT_CONTRAST_RATIO;
};
